// React hook for managing Risk game history state
import { useState, useEffect, useCallback } from 'react';
import RiskHistoryService from '../services/RiskHistoryService';

export const useRiskHistory = (contract, gameId) => {
  const [historyState, setHistoryState] = useState({
    loading: true,
    error: null,
    turnHistory: [],
    currentTurnIndex: -1, // -1 means "current live state"
    totalTurns: 0,
    isViewingHistory: false
  });

  const [historyService, setHistoryService] = useState(null);

  // Initialize history service
  useEffect(() => {
    if (contract && gameId) {
      const service = new RiskHistoryService(contract, gameId);
      setHistoryService(service);
    }
  }, [contract, gameId]);

  // Load game history
  const loadHistory = useCallback(async () => {
    if (!historyService) return;

    try {
      setHistoryState(prev => ({
        ...prev,
        loading: true,
        error: null
      }));

      const turnHistory = await historyService.fetchGameHistory();
      
      setHistoryState(prev => ({
        ...prev,
        loading: false,
        turnHistory,
        totalTurns: turnHistory.length,
        currentTurnIndex: -1 // Start with current state
      }));

    } catch (error) {
      console.error('Failed to load game history:', error);
      setHistoryState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load game history'
      }));
    }
  }, [historyService]);

  // Load history when service is ready
  useEffect(() => {
    if (historyService) {
      loadHistory();
    }
  }, [historyService, loadHistory]);

  // Navigation functions
  const navigateToTurn = useCallback((turnIndex) => {
    if (turnIndex < -1 || turnIndex >= historyState.totalTurns) return;
    
    setHistoryState(prev => ({
      ...prev,
      currentTurnIndex: turnIndex,
      isViewingHistory: turnIndex !== -1
    }));
  }, [historyState.totalTurns]);

  const goToCurrentState = useCallback(() => {
    navigateToTurn(-1);
  }, [navigateToTurn]);

  const goToPreviousTurn = useCallback(() => {
    const newIndex = Math.max(-1, historyState.currentTurnIndex - 1);
    navigateToTurn(newIndex);
  }, [historyState.currentTurnIndex, navigateToTurn]);

  const goToNextTurn = useCallback(() => {
    const maxIndex = historyState.totalTurns - 1;
    const newIndex = Math.min(maxIndex, historyState.currentTurnIndex + 1);
    navigateToTurn(newIndex);
  }, [historyState.currentTurnIndex, historyState.totalTurns, navigateToTurn]);

  const goToFirstTurn = useCallback(() => {
    if (historyState.totalTurns > 0) {
      navigateToTurn(0);
    }
  }, [historyState.totalTurns, navigateToTurn]);

  const goToLastTurn = useCallback(() => {
    if (historyState.totalTurns > 0) {
      navigateToTurn(historyState.totalTurns - 1);
    }
  }, [historyState.totalTurns, navigateToTurn]);

  // Get current viewing state
  const getCurrentViewState = useCallback(() => {
    if (historyState.currentTurnIndex === -1) {
      return {
        isLiveState: true,
        turnIndex: -1,
        turnData: null,
        displayName: 'Current State'
      };
    }

    const turnData = historyState.turnHistory[historyState.currentTurnIndex];
    return {
      isLiveState: false,
      turnIndex: historyState.currentTurnIndex,
      turnData,
      displayName: turnData ? `Turn ${turnData.turnNumber}` : 'Unknown Turn'
    };
  }, [historyState.currentTurnIndex, historyState.turnHistory]);

  // Get state for map rendering
  const getMapState = useCallback(async () => {
    const currentView = getCurrentViewState();
    
    if (currentView.isLiveState) {
      // Return current live state from contract
      try {
        return await historyService?.getCurrentGameState();
      } catch (error) {
        console.error('Error getting current state:', error);
        return null;
      }
    } else {
      // Return historical state
      const turnData = currentView.turnData;
      return turnData ? turnData.stateAfter : null;
    }
  }, [getCurrentViewState, historyService]);

  // Get summary of recent activity
  const getRecentActivity = useCallback((numTurns = 2) => {
    if (historyState.turnHistory.length === 0) return [];
    
    const recentTurns = historyState.turnHistory.slice(-numTurns);
    return recentTurns.map(turn => ({
      turnNumber: turn.turnNumber,
      player: turn.player,
      numMoves: turn.events.find(e => e.type === 'TurnSubmitted')?.args?.numMoves || 0,
      combats: turn.events.filter(e => e.type === 'Combat').length,
      conquests: turn.events.filter(e => e.type === 'TerritoryConquered').length,
      timestamp: turn.timestamp
    }));
  }, [historyState.turnHistory]);

  // Get detailed turn information
  const getTurnDetails = useCallback((turnIndex) => {
    if (turnIndex < 0 || turnIndex >= historyState.turnHistory.length) return null;
    
    const turn = historyState.turnHistory[turnIndex];
    const turnSubmitted = turn.events.find(e => e.type === 'TurnSubmitted');
    const combats = turn.events.filter(e => e.type === 'Combat');
    const conquests = turn.events.filter(e => e.type === 'TerritoryConquered');
    
    return {
      ...turn,
      summary: {
        player: turn.player,
        numMoves: turnSubmitted?.args?.numMoves || 0,
        numCombats: combats.length,
        numConquests: conquests.length,
        territoriesGained: conquests.map(c => c.args.territory),
        totalCasualties: combats.reduce((sum, c) => 
          sum + c.args.attackerLosses + c.args.defenderLosses, 0
        )
      }
    };
  }, [historyState.turnHistory]);

  // Refresh history (useful for live games)
  const refreshHistory = useCallback(async () => {
    await loadHistory();
  }, [loadHistory]);

  return {
    // State
    loading: historyState.loading,
    error: historyState.error,
    turnHistory: historyState.turnHistory,
    totalTurns: historyState.totalTurns,
    currentTurnIndex: historyState.currentTurnIndex,
    isViewingHistory: historyState.isViewingHistory,
    
    // Navigation
    navigateToTurn,
    goToCurrentState,
    goToPreviousTurn,
    goToNextTurn,
    goToFirstTurn,
    goToLastTurn,
    
    // Data access
    getCurrentViewState,
    getMapState,
    getRecentActivity,
    getTurnDetails,
    
    // Actions
    refreshHistory,
    
    // Helper flags
    canGoBack: historyState.currentTurnIndex > -1,
    canGoForward: historyState.currentTurnIndex < historyState.totalTurns - 1,
    hasHistory: historyState.totalTurns > 0
  };
};

export default useRiskHistory;